import type { Editor } from 'slate';
import type { SlateLeaf } from '@atlas/renderer';
import areSamePoints from '@atlas/tool/box/slate/areSamePoints';
import getNode from '@atlas/tool/box/slate/getNode';
import { linkToolbarClassName } from '@atlas/tool/box/slate/toggleLink';
import isMarkActive, { MarkName } from '@atlas/tool/box/slate/isMarkActive';
import hideFloatingREToolbar from '@atlas/tool/box/slate/hideFloatingREToolbar';

export default function stopMarksEditing(editor: Editor, marks: MarkName[]): boolean {
    if (!marks.length) return false;
    if (!editor.selection) return false;

    const collapsedSelection = areSamePoints(editor.selection.anchor, editor.selection.focus);
    if (!collapsedSelection) return false;

    let handled = false;

    if (marks.includes('link') && isMarkActive(editor, 'link')) {
        handled = true;
        setTimeout(() => {
            const menu = document.querySelector<HTMLElement>(`.${linkToolbarClassName}`);
            if (menu) hideFloatingREToolbar(menu);
        }, 100);
    }

    const leaf = getNode<SlateLeaf>(editor, editor.selection.focus.path);
    if (!leaf) return handled;

    if (leaf.text.length !== editor.selection.focus.offset) return handled;

    for (const mark of marks) {
        if (isMarkActive(editor, mark)) editor.removeMark(mark);
    }

    return handled;
}

// #region Tests

if (import.meta.vitest) {
    const { vi, describe, it, expect } = import.meta.vitest;
    describe('stopMarksEditing', async () => {
        it('moves cursor to the end of the mark', () => {
            const editor: Partial<Editor> = {
                selection: {
                    anchor: { path: [0, 1], offset: 3 },
                    focus: { path: [0, 1], offset: 3 },
                },
                removeMark: vi.fn(),
                children: [
                    {
                        type: 'paragraph',
                        children: [{ text: 'Hello, ' }, { text: 'World', link: 'https://atlas.so' }, { text: '!' }],
                    },
                ],
                isVoid: () => false,
                nodes: () => [[{ text: 'World', link: 'https://atlas.so' }]] as any,
            };

            expect(stopMarksEditing(editor as unknown as Editor, ['link'])).toBe(true);
            expect(editor.removeMark).not.toHaveBeenCalled();
        });
    });
}

// #endregion
